import React, { useMemo, useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LocationOption } from "@/types/locations";

export interface HierarchicalLocationSelectProps {
  locationsData: LocationOption[] | undefined;
  value: string;
  onChange: (locationId: string) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  className?: string;
  disableClearable?: boolean;
  fullWidth?: boolean;
  autoExpandToSelected?: boolean;
  variant?: "outlined" | "filled" | "standard";
  size?: "small" | "medium";
}

const HierarchicalLocationSelect: React.FC<HierarchicalLocationSelectProps> = ({
  locationsData,
  value,
  onChange,
  loading = false,
  disabled = false,
  placeholder = "Select",
  label = "",
  className = "input select",
  disableClearable = false,
  fullWidth = true,
  autoExpandToSelected = true,
  variant = "outlined",
  size = "medium",
}) => {
  const [expandedLocations, setExpandedLocations] = useState<Set<string>>(
    new Set(),
  );

  // Helper function to find a location by ID in the hierarchy
  const findLocationById = (
    locations: LocationOption[] | undefined,
    id: string,
  ): LocationOption | null => {
    if (!locations) return null;
    for (const loc of locations) {
      if (String(loc.id) === id) return loc;
      if (loc.children && loc.children.length > 0) {
        const found = findLocationById(loc.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper to find all parent IDs of a location
  const findParentIds = (
    locations: LocationOption[] | undefined,
    id: string,
    parents: string[] = [],
  ): string[] | null => {
    if (!locations) return null;
    for (const loc of locations) {
      if (String(loc.id) === id) {
        return parents;
      }
      if (loc.children && loc.children.length > 0) {
        const found = findParentIds(loc.children, id, [
          ...parents,
          String(loc.id),
        ]);
        if (found) return found;
      }
    }
    return null;
  };

  // Auto-expand to selected location when value changes or component mounts
  useEffect(() => {
    if (autoExpandToSelected && value && locationsData) {
      const parentIds = findParentIds(locationsData, value) || [];
      setExpandedLocations(new Set(parentIds));
    } else if (!value) {
      // Reset expansion when value is cleared
      setExpandedLocations(new Set());
    }
  }, [value, locationsData, autoExpandToSelected]);

  // Toggle expand/collapse for a location
  const toggleLocationExpand = (locationId: string) => {
    setExpandedLocations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(locationId)) {
        newSet.delete(locationId);
      } else {
        newSet.add(locationId);
      }
      return newSet;
    });
  };

  // Build visible locations list respecting expanded state
  const buildVisibleLocationsList = useMemo(() => {
    const result: Array<
      LocationOption & { level: number; hasChildren: boolean }
    > = [];

    function traverse(items: LocationOption[], level: number) {
      for (const item of items) {
        const hasChildren =
          Array.isArray(item.children) && item.children.length > 0;
        result.push({ ...item, level, hasChildren });

        // Only add children if this node is expanded
        if (
          hasChildren &&
          expandedLocations.has(String(item.id)) &&
          item.children
        ) {
          traverse(item.children, level + 1);
        }
      }
    }

    if (locationsData) {
      traverse(locationsData, 0);
    }

    return result;
  }, [locationsData, expandedLocations]);

  return (
    <Autocomplete
      options={buildVisibleLocationsList}
      loading={loading}
      getOptionLabel={(option) => option.name || ""}
      getOptionKey={(option) => option.id || ""}
      isOptionEqualToValue={(option, value) =>
        String(option.id) === String(value?.id)
      }
      value={
        locationsData && value ? findLocationById(locationsData, value) : null
      }
      className={className}
      onChange={(_, newValue) => {
        onChange(newValue ? String(newValue.id) : "");
      }}
      renderOption={(props, option) => {
        const typedOption = option as LocationOption & {
          level: number;
          hasChildren: boolean;
        };
        const isExpanded = expandedLocations.has(String(typedOption.id));

        return (
          <Box
            component="li"
            {...props}
            key={typedOption.id}
            sx={{
              display: "flex !important",
              alignItems: "center !important",
              paddingLeft: `${typedOption.level * 24 + 16}px !important`,
              gap: "8px",
            }}
          >
            {typedOption.hasChildren ? (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLocationExpand(String(typedOption.id));
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  minWidth: "24px",
                  height: "24px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                    borderRadius: "4px",
                  },
                }}
              >
                {isExpanded ? (
                  <ExpandMoreIcon fontSize="small" />
                ) : (
                  <ChevronRightIcon fontSize="small" />
                )}
              </Box>
            ) : (
              <Box sx={{ minWidth: "24px" }} />
            )}
            <Typography
              sx={{
                fontSize: "16px",
              }}
            >
              {typedOption.name}
            </Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="primary" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      disabled={disabled || loading}
      disableClearable={disableClearable}
      onOpen={() => {
        // Auto-expand to show selected location when dropdown opens
        if (autoExpandToSelected && value && locationsData) {
          const parentIds = findParentIds(locationsData, value) || [];
          setExpandedLocations(new Set(parentIds));
        }
      }}
    />
  );
};

export default HierarchicalLocationSelect;
